import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useCurrency from '@hooks/useCurrency';
import { Button } from '@ui';
import { FormInput } from 'components/Form/next';
import { FormInputProps } from 'components/Form/next/props';
import { get } from 'lodash';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props extends FormInputProps {
    allowEditing?: boolean;
}

export const InvoiceCreditsFields = ({ name, allowEditing = true }: Props) => {
    const { t } = useTranslation('invoice');
    const {
        control,
        watch,
        formState: { errors }
    } = useFormContext();
    const { getSymbol } = useCurrency();
    const currencyCode = watch('currency');
    const error = get(errors, name)?.message;
    const { fields, append, remove } = useFieldArray({
        control,
        name
    });

    const inputProps: Partial<React.ComponentProps<typeof FormInput>> = { withoutShifting: true, size: 'sm' };
    return (
        <>
            {fields.map((field, index) => (
                <div className="tw-mb-2 tw-flex tw-items-baseline tw-gap-4" key={field.id}>
                    <FormInput
                        {...inputProps}
                        placeholder={t('invoiceForm.fields.description')}
                        name={`${name}.${index}.description`}
                        disabled={!allowEditing}
                        fullWidth
                        flush
                    />
                    <FormInput
                        {...inputProps}
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder={t('invoiceForm.creditsFields.value')}
                        name={`${name}.${index}.amount`}
                        disabled={!allowEditing}
                        flush
                    />
                    <FormInput.Switch
                        labelOn="%"
                        className="tw-mt-1"
                        labelOff={getSymbol(currencyCode).substring(0, 1)}
                        name={`${name}.${index}.percentage`}
                        disabled={!allowEditing}
                    />
                    <Button
                        onPress={() => remove(index)}
                        color="error"
                        auto
                        css={{ mt: 3, px: '$4' }}
                        size={inputProps.size}
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        disabled={!allowEditing}
                    />
                </div>
            ))}
            <Button
                disabled={!allowEditing}
                size="xs"
                type="button"
                block
                className="tw-mt-2"
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() =>
                    append(
                        {
                            amount: '1',
                            percentage: false
                        },
                        { shouldFocus: true }
                    )
                }
            >
                {t('invoiceForm.creditsFields.addDiscount')}
            </Button>
            {error && <span className="tw-text-xs tw-text-error">{error}</span>}
        </>
    );
};
